import styled from '@emotion/styled'
import {COLORS} from '../constants'

/**
 * The clickable investor-count control in the card meta row. Styled to sit inline with the
 * neighbouring meta items but reads as interactive (hover background), and greys out when the
 * offer has no investors.
 */
export const CountButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: -2px -4px;
  padding: 2px 4px;
  border: none;
  border-radius: 6px;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.mutedBg};
    color: ${COLORS.foreground};
  }

  &:disabled {
    cursor: default;
    color: inherit;
  }

  &:disabled:hover {
    background: none;
  }
`

/** Popover body wrapper; caps width to the card / viewport and clips the rounded corners. */
export const PopoverBody = styled.div`
  width: min(92vw, 640px);
  color: ${COLORS.foreground};
`

/** Sticky header of the popover: owner + count on the left, AUM on the right. */
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid ${COLORS.border};
  background-color: ${COLORS.mutedBg};
`

/** Small uppercase caption used for the "Investors" and "AUM" labels. */
export const HeaderCaption = styled.div`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLORS.mutedText};
`

/** Owner name + total-count line under the "Investors" caption. */
export const HeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.foreground};
`

/** Right-aligned AUM figure in the header. */
export const HeaderAum = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  color: ${COLORS.success};
`

/**
 * Scroll container so a long book scrolls while the header/footer stay put.
 *
 * The cap is `min(52vh, 380px)`: 380px matches the design, and the vh term keeps the popover
 * inside a short viewport without ever reflowing the page behind it. Eight rows (~33px each)
 * fit under the cap, which is where the AC's "scrolls past 8 rows" comes from — no row
 * counting needed.
 *
 * The scrollbar is styled explicitly, not left to the platform: macOS overlay scrollbars stay
 * hidden until a scroll gesture, which would make a cut-off list look finished — exactly the
 * failure the AC calls out, and one that would not reproduce on a reviewer's machine.
 * Styling `::-webkit-scrollbar` at all opts WebKit out of overlay mode.
 */
export const ScrollArea = styled.div`
  max-height: min(52vh, 380px);
  overflow: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${COLORS.border};
    border-radius: 4px;
  }
`

/**
 * The investor table.
 *
 * `min-width` is what makes the mobile story work: at 375px the popover is full-width
 * (`PopoverBody` is `min(92vw, 640px)`), the table overflows it, and `ScrollArea`'s existing
 * `overflow: auto` turns that into horizontal scroll. Every column stays reachable — the AC
 * forbids any column silently disappearing — with no separate mobile layout to maintain.
 */
export const Table = styled.table`
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 12px;
`

/** Sticky column-header row. */
export const HeadRow = styled.tr`
  text-align: left;
  color: ${COLORS.mutedText};
`

type CellProps = {align?: 'left' | 'right'}

/** Column header cell. */
export const Th = styled.th<CellProps>`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 12px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: ${({align}) => align ?? 'left'};
  background-color: ${COLORS.cardBg};
  border-bottom: 1px solid ${COLORS.border};
`

/** One investor row; zebra-striped so rows stay scannable at 12px (AC). */
export const Row = styled.tr`
  &:nth-of-type(even) {
    background-color: ${COLORS.mutedBg}66;
  }

  /* Hover must win over the stripe, so it comes last. */
  &:hover {
    background-color: ${COLORS.mutedBg};
  }
`

/** Body cell. */
export const Td = styled.td<CellProps>`
  padding: 8px 12px;
  text-align: ${({align}) => align ?? 'left'};
  border-bottom: 1px solid ${COLORS.border};
  white-space: nowrap;
`

/** Investor name cell, emphasised. */
export const NameCell = styled(Td)`
  font-weight: 500;
  color: ${COLORS.foreground};
`

/**
 * Inner clamp for the investor name: a block element carries the width cap + ellipsis reliably,
 * whereas `max-width`/`text-overflow` on the `&lt;td&gt;` itself is ignored under the table's default
 * auto layout. A long name is truncated with an ellipsis; the full name is exposed via a `title`.
 */
export const NameText = styled.span`
  display: block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** Funds cell, emphasised in the success colour. */
export const FundsCell = styled(Td)`
  font-weight: 600;
  color: ${COLORS.success};
`

/** Muted created-date cell. */
export const CreatedCell = styled(Td)`
  color: ${COLORS.mutedText};
`

/** Mailto link in the email column. */
export const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${COLORS.brand};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

/**
 * A masked address (`j***@domain`) in the email column (PT-5709).
 *
 * Deliberately not an `<a>`: it is not a real address, so it must not be actionable. It is also
 * styled as muted body text rather than in the brand colour, so the row reads as "withheld"
 * instead of as a link that failed to work.
 */
export const MaskedEmailText = styled.span`
  color: ${COLORS.mutedText};
  cursor: default;
`

/** Sticky totals row summing funds (= AUM) and share (= 100%). */
export const TotalRow = styled.tr`
  position: sticky;
  bottom: 0;
  font-weight: 600;
  color: ${COLORS.foreground};

  & > td {
    padding: 8px 12px;
    background-color: ${COLORS.cardBg};
    border-top: 1px solid ${COLORS.border};
  }
`

/** "Total" caption in the footer's first cell. */
export const TotalLabel = styled.td`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLORS.mutedText};
`

/** Search row; sits between the header and the scroll area so it stays put while rows scroll. */
export const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid ${COLORS.border};
`

/** The filter input. */
export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid ${COLORS.border};
  border-radius: 6px;
  background: none;
  color: ${COLORS.foreground};
  font: inherit;
  font-size: 12px;

  &::placeholder {
    color: ${COLORS.mutedText};
  }
`

/** "4 of 137 investors" — announced politely while filtering. */
export const SearchCount = styled.div`
  flex-shrink: 0;
  font-size: 11px;
  color: ${COLORS.mutedText};
`

/** One-click reset for an active filter. */
export const ClearButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: none;
  color: ${COLORS.mutedText};
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.mutedBg};
    color: ${COLORS.foreground};
  }
`

/** Shown instead of an empty table body when a filter matches nothing. */
export const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 12px;
  color: ${COLORS.mutedText};
`

/**
 * A column header rendered as a button so sorting is keyboard-reachable. Inherits the <th>'s
 * uppercase caption styling so the header looks identical to a non-sortable one until hovered.
 */
export const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;

  &:hover {
    color: ${COLORS.foreground};
  }
`

/**
 * The ▲/▼ sort direction indicator; dimmed until its column is the active sort. Rendered as
 * generated CSS content (not a text child) so it never leaks into `textContent` — several
 * regression tests assert a header's textContent equals its raw i18n key exactly.
 *
 * The prop is named `arrowDir`, not `dir`/`direction` — both of those are recognised by
 * emotion's default prop-validity filter (`dir` as an HTML global attribute, `direction` as an
 * SVG presentation attribute) and get forwarded straight to the DOM. `arrowDir` isn't recognised
 * by that filter, so it stays off the rendered element; verified with
 * `@emotion/is-prop-valid`.
 */
export const SortArrow = styled.span<{active: boolean; arrowDir: 'asc' | 'desc'}>`
  display: inline-block;
  width: 8px;
  font-size: 8px;
  visibility: ${({active}) => (active ? 'visible' : 'hidden')};

  &::after {
    content: '${({arrowDir}) => (arrowDir === 'asc' ? '▲' : '▼')}';
  }
`
